import type { HTMLAttributes, JSX } from 'react';

import { cn } from '@/lib/utils';

/**
 * A plain, dependency-free progress bar. Renders the caller's own
 * `value` (0-100, clamped) verbatim - never computes or infers a
 * percentage itself, matching this codebase's rule that a metric is
 * always the backend's own number (`ProgressStatsGrid`'s doc comment).
 */
export function Progress({
  value,
  className,
  ...props
}: { readonly value: number } & Omit<HTMLAttributes<HTMLDivElement>, 'children'>): JSX.Element {
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-secondary', className)}
      {...props}
    >
      <div
        className="h-full rounded-full bg-primary transition-all"
        style={{ width: `${String(clamped)}%` }}
      />
    </div>
  );
}
